import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, InputLabel, TextField, Autocomplete, createFilterOptions } from "@mui/material";
import { useSelector } from "react-redux";
import { useCreateGroupMutation, useGetBinsQuery, useGetAllBinGroupsQuery } from "../../../../../shell/services/mediaManager";
import { Group } from "../../../../../shell/services/types";
import { useParams } from "../../../../../shell/hooks/useParams";
interface Props {
  open: boolean;
  onClose: () => void;
  id?: string;
  binId?: string;
}
export const NewFolderDialog = ({
  open,
  onClose,
  id,
  binId
}: Props) => {
  const {
    t
  } = useTranslation();
  const [name, setName] = useState(t("media.newFolderDialogDefaultFolderName"));
  const [selectedGroup, setSelectedGroup] = useState<Group | null>();
  const [params, setParams] = useParams();
  const [createGroup, {
    isLoading: isCreatingGroup,
    isSuccess,
    data
  }] = useCreateGroupMutation();
  const instanceId = useSelector((state: any) => state.instance.ID);
  const ecoId = useSelector((state: any) => state.instance.ecoID);
  const {
    data: bins,
    isLoading: isLoadingBins
  } = useGetBinsQuery({
    instanceId,
    ecoId
  });
  const currentBin = bins?.find(bin => bin.id === binId);
  const defaultBin = bins?.find(bin => bin.default === true);
  const {
    data: allBinGroups,
    isLoading: isLoadingAllBinGroups
  } = useGetAllBinGroupsQuery(bins?.map(bin => bin.id), {
    skip: !bins?.length
  });
  useEffect(() => {
    if (isSuccess) {
      setParams(data?.data?.[0]?.id, "newFolderId");
      onClose();
    }
  }, [isSuccess]);
  useEffect(() => {
    if (allBinGroups) {
      if (binId) {
        setSelectedGroup(allBinGroups?.flat()?.find(group => group.id === id) || {
          name: currentBin?.name,
          id: binId,
          group_id: binId,
          bin_id: binId
        });
      } else {
        setSelectedGroup({
          name: defaultBin?.name,
          id: defaultBin?.id,
          group_id: defaultBin?.id,
          bin_id: defaultBin?.id
        });
      }
    }
  }, [allBinGroups, id, currentBin, binId]);
  const handleCreate = () => {
    createGroup({
      body: {
        name,
        group_id: selectedGroup?.id,
        bin_id: binId ?? selectedGroup?.bin_id
      }
    });
  };
  const sortedBinGroups = useMemo(() => {
    if (allBinGroups?.length && bins?.length) {
      if (binId) {
        return [{
          name: currentBin?.name ?? t("media.newFolderDialogNone"),
          bin_id: binId,
          group_id: binId,
          id: binId
        }, ...allBinGroups.flat()].filter(group => group.bin_id === binId).sort((a, b) => a?.name?.localeCompare(b?.name));
      } else {
        const allBins = bins.map(bin => ({
          name: bin.name,
          bin_id: bin.id,
          group_id: bin.id,
          id: bin.id
        }));
        return [...allBins, ...allBinGroups.flat()].sort((a, b) => a?.name?.localeCompare(b?.name));
      }
    }
    return [];
  }, [allBinGroups, binId, bins, t]);
  const loading = isLoadingBins || isLoadingAllBinGroups;
  return <Dialog open={open} onClose={onClose} fullWidth maxWidth={"xs"} data-sentry-element="Dialog" data-sentry-component="NewFolderDialog" data-sentry-source-file="NewFolderDialog.tsx">
      <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="NewFolderDialog.tsx">{t("media.newFolderDialogTitle")}</DialogTitle>
      <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="NewFolderDialog.tsx">
        <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="NewFolderDialog.tsx">{t("media.newFolderDialogParentFolderLabel")}</InputLabel>
        <Autocomplete data-cy="newFolderParentSelector" size="small" fullWidth loading={loading} value={selectedGroup} disableClearable options={sortedBinGroups?.length ? sortedBinGroups : []} renderInput={params => <TextField {...params} hiddenLabel />} renderOption={(props, option) => <li {...props} key={option.id}>
              {option.name}
            </li>} getOptionLabel={(option: any) => option.name} filterOptions={createFilterOptions({
        stringify: option => option.name
      })} isOptionEqualToValue={(option, value) => option.id === value.id} onChange={(event, newValue) => setSelectedGroup(newValue as Group)} sx={{
        mb: 3
      }} data-sentry-element="Autocomplete" data-sentry-source-file="NewFolderDialog.tsx" />
        <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="NewFolderDialog.tsx">{t("media.newFolderDialogFolderNameLabel")}</InputLabel>
        <TextField autoFocus value={name} hiddenLabel size="small" fullWidth onChange={evt => setName(evt.target.value)} onFocus={evt => evt.target.select()} onKeyPress={event => event.key === "Enter" && handleCreate()} data-sentry-element="TextField" data-sentry-source-file="NewFolderDialog.tsx" />
      </DialogContent>
      <DialogActions data-sentry-element="DialogActions" data-sentry-source-file="NewFolderDialog.tsx">
        <Button onClick={onClose} color="inherit" data-sentry-element="Button" data-sentry-source-file="NewFolderDialog.tsx">
          {t("common.cancel")}
        </Button>
        <Button loading={isCreatingGroup} disabled={loading || !selectedGroup || selectedGroup?.bin_id === null} variant="contained" onClick={handleCreate} data-sentry-element="Button" data-sentry-source-file="NewFolderDialog.tsx">
          {t("common.create")}
        </Button>
      </DialogActions>
    </Dialog>;
};